import React, { useEffect, useState } from "react";
import { ChevronLeftIcon, XIcon } from "@heroicons/react/outline";
import SelectChangeForm from "./Forms/SelectChange";
import { INITIAL_TABS } from "../config/tabs";
import Verify from "components/lib/Shared/Verify";
import { Formik, Field, Form, useFormikContext } from "formik";
import Button from "components/lib/Shared/Button";
import ResidentialDetails from "./Forms/ResidentialDetails";
import NamesForm from "./Forms/Name";
import { getDocumentDataForEmployee } from "config/documentData";
import { useDispatch, useSelector } from "react-redux";
import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import { createEmployeeAsync } from "state/slices/employee";
import Tabs from "components/lib/Shared/Tabs";
import { useToast } from "hooks/useToast";
import { addAccessRequest } from "state/slices/accessRequest";
import { PlusIcon } from "components/lib/Shared/Icons/sflIcons";
import _merge from "lodash/merge";
import {
  getProfessionalDetailsAsync,
  resetProfessionalDetailsSlice,
} from "state/slices/professionalDetails";
import ProfessionalDetail from "./Forms/ProfessionalDetail";
import getValidationSchema from "./validation";
import { handleFilesUpload } from "utils/filesUtils";
import InviteExistingUser from "components/lib/Shared/Verify/InviteExistingUser";
import AssignPermissionsSelection from "components/lib/Shared/CompanyDashboard/AssignPermission/Selection";

const defaultRoles = ["Company Secretary", "Advocate", "Auditor"];

function ProfessionalDetailsWatcher({ dispatch }) {
  const { values } = useFormikContext();
  useEffect(() => {
    if (values.userId) {
      dispatch(getProfessionalDetailsAsync({ user: values.userId }));
    } else {
      dispatch(resetProfessionalDetailsSlice());
    }
  }, [dispatch, values.userId]);
  return null;
}
const AddEmployee = ({ setEmployee, setRecallEmployeeTable, formType }) => {
  const { toast } = useToast(5000);
  const currentUser = useSelector((state) => state.userSlice);
  const { company } = useSelector((state) => state.companyDashboardSlice);
  const { organization } = useSelector((state) => state.organizationsSlice);
  const [formFieldsToRender, setFormFieldsToRender] = useState({});
  const dispatch = useDispatch();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [showIdentificationSection, setShowIdentificationSection] =
    useState(true);
  const [isIdAssociated, setIsIdAssociated] = useState(null);
  const [verificationValue, setVerifyingValue] = useState("");
  const [isAccessAllowed, setIsAccessAllowed] = useState(false);
  const [isAccessRevoked, setIsAccessRevoked] = useState(false);
  const [inviteNewUserWorkflow, setInviteNewUserWorkflow] = useState(false);
  const [disableForm, setDisableForm] = useState(false);
  const [inviteExistingUser, setInviteExistingUser] = useState(false);
  const [selectedDetails, setSelectedDetail] = useState(null);
  const [addNewDetail, setAddNewDetail] = useState(false);

  const [selectedTab, setSelectedTab] = useState(
    INITIAL_TABS[selectedTabIndex]
  );
  const handleNewUserWorkflow = () => {
    setInviteNewUserWorkflow(true);
  };
  const professionalDetails = useSelector(
    (state) => state.professionalDetailsSlice.items
  );
  const documentData = getDocumentDataForEmployee(
    formFieldsToRender.idTypeSelection
  );
  const handleExitWorkflow = () => {
    setEmployee(false);
    setInviteNewUserWorkflow(false);
    setInviteExistingUser(false);
  };

  const closeVerifyProcess = () => {
    setInviteExistingUser(false);
    setRecallEmployeeTable(true);
    setEmployee(false);
  };
  const assignedRoles = professionalDetails.map(({ capacity }) => capacity);
  const capacities = defaultRoles.filter(
    (capacity) => !assignedRoles.includes(capacity)
  );

  const handleSubmit = async (values) => {
    setFormFieldsToRender({
      officialMethod: values.designation,
      idTypeSelection: values.idType,
      moduleType: "Manage Module",
    });

    if (selectedTabIndex < INITIAL_TABS.length - 1) {
      setSelectedTabIndex(selectedTabIndex + 1);
    } else {
      const uploadedFiles = await handleFilesUpload(values.fileUploads);
      let payload = {
        user: values.userId,
        formType,
        isLegalDepartment: values.legalDepartment === "yes",
        uploadedFiles,
        ...values,
      };
      if (formType === "company") {
        payload.company = company._id;
      } else {
        payload.organization = organization._id;
      }
      const response = await dispatch(createEmployeeAsync({ data: payload }));
      await dispatch(
        addAccessRequest({
          data: {
            initiatedBy: currentUser._id,
            initiatedTo: values.userId,
            employee: response.payload.employee._id,
            appointedAs: "Employee",
            initiatedToEmail: values.email,
            ...(formType === "company"
              ? { company: company._id }
              : { organization: organization._id }),
          },
        })
      );
      toast(
        "success",
        "The request has been successfully submitted to the registrar."
      );
      setRecallEmployeeTable(true);
      setEmployee(false);
    }
  };

  const goToPrevStep = () => {
    setSelectedTabIndex(selectedTabIndex - 1);
  };

  const defaultAccessTypeSchema = {
    startDate: null,
    endDate: null,
    revokedAt: null,
    duration: "indefinite",
  };

  return (
    <div className="employee-container">
      <Formik
        initialValues={{
          isProfessional: "no",
          legalDepartment: "no",
        }}
        validationSchema={getValidationSchema(
          INITIAL_TABS[selectedTabIndex].id,
          documentData
        )}
        onSubmit={handleSubmit}
      >
        {({
          errors,
          setFieldValue,
          values,
          touched,
          setTouched,
          isSubmitting,
          resetForm,
        }) => {
          const watcher = <ProfessionalDetailsWatcher dispatch={dispatch} />;

          const handleEditProfessionalDetail = (professionalDetail) => {
            setSelectedDetail(professionalDetail);
            setAddNewDetail(false);
          };
          const handleAddNewProfessionalDetail = () => {
            setSelectedDetail(null);
            setAddNewDetail(true);
          };
          return (
            <>
              {inviteExistingUser &&
              INITIAL_TABS[selectedTabIndex].id === "generalInformation" ? (
                <>
                  <div className="sm:-mt-4">
                    <Tabs
                      tabs={INITIAL_TABS}
                      selectedTabIndex={selectedTabIndex}
                      setSelectedTabIndex={setSelectedTabIndex}
                    />
                  </div>
                  <Button
                    className="flex ml-auto my-2"
                    onClick={handleExitWorkflow}
                    variant="text"
                  >
                    <XIcon className="block w-6 h-6" aria-hidden="true" />
                  </Button>

                  <InviteExistingUser
                    forEmployee
                    formType={formType}
                    userId={values.userId}
                    idType={values.idType}
                    designation={values.designation}
                    closeVerifyProcess={closeVerifyProcess}
                    userEmailAddress={values.email}
                    values={values}
                    // inviteOfficial={addOrEditOfficial}
                  />
                </>
              ) : (
                <Form>
                  {watcher}
                  <div className="mt-4 flex items-center">
                    <p className="mr-4">
                      Is this user part of the legal department?
                    </p>
                    <div className="flex space-x-4">
                      <label className="flex items-center text-gray-500 text-sm mb-1">
                        <Field
                          type="radio"
                          name="legalDepartment"
                          value="yes"
                          className="mr-2"
                          disabled={
                            INITIAL_TABS[selectedTabIndex].id !== "idType"
                          }
                        />
                        Yes
                      </label>
                      <label className="flex items-center text-gray-500 text-sm mb-1">
                        <Field
                          type="radio"
                          name="legalDepartment"
                          value="no"
                          className="mr-2"
                          disabled={
                            INITIAL_TABS[selectedTabIndex].id !== "idType"
                          }
                        />
                        No
                      </label>
                    </div>
                  </div>
                  {errors.legalDepartment && (
                    <small className="text-red-500 mt-2 py-4 px-1 font-semibold">
                      {errors.legalDepartment}
                    </small>
                  )}

                  <div className="sm:-mt-2">
                    <Tabs
                      tabs={INITIAL_TABS}
                      selectedTabIndex={selectedTabIndex}
                    />
                  </div>

                  <Button
                    className="flex ml-auto my-2"
                    onClick={handleExitWorkflow}
                    variant="text"
                  >
                    <XIcon className="block w-6 h-6" aria-hidden="true" />
                  </Button>

                  {INITIAL_TABS[selectedTabIndex].id === "idType" && (
                    <div className="container mx-auto px-4">
                      <SelectChangeForm
                        setFieldValue={setFieldValue}
                        formStepErrors={errors}
                        values={values}
                      />
                    </div>
                  )}

                  {INITIAL_TABS[selectedTabIndex].id ===
                    "generalInformation" && (
                    <>
                      <div className="px-4 space-y-4 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6  mt-3">
                        <Verify
                          idType={values.idType}
                          designation={"Partner"}
                          forEmployees={true}
                          setShowIdentificationSection={
                            setShowIdentificationSection
                          }
                          verificationValue={verificationValue}
                          isIdAssociated={isIdAssociated}
                          isAccessAllowed={isAccessAllowed}
                          isAccessRevoked={isAccessRevoked}
                          setVerifyingValue={setVerifyingValue}
                          setIsIdAssociated={setIsIdAssociated}
                          setDisableForm={setDisableForm}
                          setIsAccessAllowed={setIsAccessAllowed}
                          setIsAccessRevoked={setIsAccessRevoked}
                          setInviteExistingUser={setInviteExistingUser}
                          setFieldValue={setFieldValue}
                          handleNewUserWorkflow={handleNewUserWorkflow}
                        />
                        {showIdentificationSection && (
                          <>
                            {[
                              "Kenyan Citizen",
                              "Foreign Resident",
                              "Foreigner",
                            ].includes(values.idType) && (
                              <Field
                                name="names"
                                className="mt-4"
                                component={NamesForm}
                                values={values}
                                disableForm={disableForm}
                                formStepErrors={errors}
                                disableVerificationField={
                                  isIdAssociated === false
                                }
                              />
                            )}
                          </>
                        )}
                      </div>
                    </>
                  )}

                  {INITIAL_TABS[selectedTabIndex].id ===
                    "residentialDetails" && (
                    <>
                      <ResidentialDetails
                        values={values}
                        disableForm={disableForm}
                        setFieldValue={setFieldValue}
                        company={company}
                        formType={formType}
                        organization={organization}
                        disable={disableForm}
                        formStepErrors={errors}
                        touched={""}
                        isNonResident={"No"}
                      />
                    </>
                  )}

                  {INITIAL_TABS[selectedTabIndex].id === "uploadDocuments" && (
                    <div className="mt-4 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md">
                      <div className="px-4 py-6 sm:p-6">
                        {[
                          "Kenyan Citizen",
                          "Foreign Resident",
                          "Foreigner",
                        ].includes(values.idType) &&
                          !values?.UserId && (
                            <div className="mb-2">
                              <p>
                                Please upload the correct documents as
                                instructed
                              </p>
                            </div>
                          )}
                        <Field
                          name="fileUploads"
                          component={FileUploadForm}
                          documentData={getDocumentDataForEmployee(
                            values.idType
                          )}
                          onlyPreview={values?.userId ? true : false}
                          showDocumentLabel
                        />
                      </div>
                    </div>
                  )}

                  {INITIAL_TABS[selectedTabIndex].id ===
                    "professionalDocuments" && (
                    <>
                      <div className="px-4 space-y-4 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6  mt-3">
                        <p>
                          Do you want to add Professional Details to the
                          profile?
                        </p>
                        <div className="flex space-x-4">
                          <label className="flex items-center text-gray-500 text-sm mb-1 mt-2">
                            <Field
                              type="radio"
                              name="isProfessional"
                              value="yes"
                              className="mr-2"
                            />
                            Yes
                          </label>
                          <label className="flex items-center text-gray-500 text-sm mb-1 mt-2">
                            <Field
                              type="radio"
                              name="isProfessional"
                              value="no"
                              className="mr-2"
                            />
                            No
                          </label>
                        </div>
                      </div>

                      {values.isProfessional === "yes" && (
                        <>
                          {professionalDetails?.length > 0 ? (
                            <div className="px-4 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
                              <h3 className="my-4 text-lg font-medium leading-6 text-gray-900">
                                Professional qualifications
                              </h3>
                              {professionalDetails.map(
                                (
                                  professionalDetail,
                                  professionalDetailIndex
                                ) => (
                                  <div key={professionalDetail._id}>
                                    <ProfessionalDetail
                                      professionalDetail={professionalDetail}
                                      isViewMode={
                                        selectedDetails?._id !==
                                        professionalDetail._id
                                      }
                                      isEditMode={
                                        selectedDetails?._id ===
                                        professionalDetail._id
                                      }
                                      handleEdit={handleEditProfessionalDetail}
                                    />
                                    {professionalDetailIndex <
                                      professionalDetails.length - 1 && <hr />}
                                  </div>
                                )
                              )}
                              {addNewDetail ? (
                                <ProfessionalDetail
                                  isViewMode={false}
                                  setAddNewDetail={setAddNewDetail}
                                  capacities={capacities}
                                  user={values.userId}
                                  email={values.email}
                                />
                              ) : (
                                professionalDetails.length < 3 && (
                                  <Button
                                    type="button"
                                    onClick={handleAddNewProfessionalDetail}
                                    className="flex ml-auto mr-auto mt-4 uppercase"
                                    variant="text"
                                    preIcon={PlusIcon}
                                  >
                                    Add New Qualification
                                  </Button>
                                )
                              )}
                            </div>
                          ) : (
                            <div className="px-4 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
                              <ProfessionalDetail
                                isViewMode={false}
                                setAddNewDetail={setAddNewDetail}
                                capacities={capacities}
                                user={values.userId}
                                email={values.email}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {INITIAL_TABS[selectedTabIndex].id ===
                    "RolesAndPermissions" && (
                    <AssignPermissionsSelection
                      initialValues={{
                        accessType: {
                          standardUser: defaultAccessTypeSchema,
                          orgAdmin: defaultAccessTypeSchema,
                          brsOps: defaultAccessTypeSchema,
                          superAdmin: defaultAccessTypeSchema,
                        },
                        dashboardPermissions: [],
                        dashboardRoles: [],
                      }}
                      setFieldValueToParent={setFieldValue}
                      hasRoles={true}
                      employCreateWorkflow={true}
                    />
                  )}
                  {showIdentificationSection && (
                    <div className="flex justify-between items-center mt-4">
                      {selectedTabIndex > 0 ? (
                        <button
                          type="button"
                          onClick={goToPrevStep}
                          className="flex items-start float-right py-2 font-semibold rounded text-primary mt-7"
                        >
                          <span className="flex items-center mr-4 h-7">
                            <ChevronLeftIcon
                              className="flex-shrink-0 w-5 h-5"
                              aria-hidden="true"
                            />
                          </span>
                          <p className="font-semibold">Go back</p>
                        </button>
                      ) : (
                        <div></div>
                      )}

                      <Button type="submit" isLoading={isSubmitting}>
                        {selectedTabIndex === INITIAL_TABS.length - 1
                          ? "Assign"
                          : "Proceed"}
                      </Button>
                    </div>
                  )}
                </Form>
              )}
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default AddEmployee;
